import {
    AppEntitlementUserBindingFeedValue,
    AppEntitlementUserBindingHistoryValue, AppEntitlementUserBindingWithReasonsValue,
    AppEntitlementValue,
    AppEntitlementValueWithDeactivatedOwner,
    AppEntitlementValueWithOneOwner,
    AppEntitlementValueWithRevokable,
    AppEntitlementWithUserBindingValue,
    AppEntitlementWithUserBindingValueWithId,
    AppUserValueWithEntitlementValue,
    CatalogEntryValue,
    expandAppEntitlementView,
    expandEntitlementPerAppView,
} from 'lib/actions/apps/app-entitlement'
import { AppEntitlementProxyValue, expandAppEntitlementProxyView } from 'lib/actions/apps/app-proxy-entitlement'
import { expandValue } from 'lib/rpcts'
import { getRefString } from 'lib/websocket-notify'
import { App } from 'pbts/c1/api/app/v1/app'
import {
    AccessProfilesForUser,
    AncestralBindingRef,
    AppEntitlement,
    AppEntitlementAutomation,
    AppEntitlementAutomationRef,
    AppEntitlementSearchServiceSearchAncestorsResponse,
    AppEntitlementSearchServiceSearchExpiringAccessForUserResponse,
    BulkGetAppEntitlementsRequest,
    BulkGetAppEntitlementsResponse,
    GetAccessProfilesForGrantsRequest,
    GetAccessProfilesForGrantsResponse,
    GetAppEntitlementResponse, ListAppEntitlementsResponse,
    SearchAppEntitlementsWithDeactivatedOwnerResponse,
    SearchAppEntitlementsWithOneOwnerResponse,
    SearchAppEntitlementUserBindingSourcesResponse,
    SearchAppEntitlementUserBindingDestinationsResponse,
    SearchAppEntitlementCorrelationForAccessProfileResponse,
    AppEntitlementCorrelation,
    SearchAppEntitlementsWithRevokableForAppUserResponse,
} from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementsClient, AppEntitlementSearchServiceClient } from 'pbts/c1/api/app/v1/app_entitlement.client'
import {
    BulkCreateAppEntitlementProxyWithDestinationsResponse,
    SearchAppEntitlementProxyBindingsResponse,
    EnableAppEntitlementProxyResponse,
    DisableAppEntitlementProxyResponse,
} from 'pbts/c1/api/app/v1/app_entitlement_proxy'
import { AppEntitlementsProxyClient, AppEntitlementsProxySearchClient } from 'pbts/c1/api/app/v1/app_entitlement_proxy.client'
import {
    AppEntitlementUserBinding,
    AppEntitlementUserBindingExpanded, AppEntitlementUserBindingWithReasons,
    GrantOriginWithData, ListMembershipsResponse,
    SearchAppUsersWithStandingHighRiskGrantsResponse,
    SearchGrantFeedResponse,
    SearchGrantReasonsResponse,
    SearchPastGrantsResponse,
} from 'pbts/c1/api/app/v1/app_entitlement_user_binding'
import { AppEntitlementUserBindingServiceClient } from 'pbts/c1/api/app/v1/app_entitlement_user_binding.client'
import { AppEntitlementRef, AppEntitlementUserBindingRef } from 'pbts/c1/api/app/v1/app_refs'
import { AppUser } from 'pbts/c1/api/app/v1/app_user'
import { AttributeSearchClient } from 'pbts/c1/api/attribute/v1/attribute.client'
import { FacetValue } from 'pbts/c1/api/search/v1/facet'
import { MinTask, SearchTermsResponse } from 'pbts/c1/api/task/v1/search'
import { TaskSearchServiceClient } from 'pbts/c1/api/task/v1/search.client'
import { Task, TaskCountRefreshRef } from 'pbts/c1/api/task/v1/task'
import { User } from 'pbts/c1/api/user/v1/user'
import { Any } from 'pbts/google/protobuf/any'
import { createMultiGetApi, MultiGetOpts } from 'redux_toolkit/lib/createMultiGetApi'
import { createMutationApi } from 'redux_toolkit/lib/createMutationApi'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'
import { createUnaryApi } from './lib/createUnaryApi'
import { expanderizeEntitlementResponse } from './lib/expanderizer'

export const ENTITLEMENT_CLI_ALIAS_REGEX = /^[a-z0-9][-a-z0-9_.]{0,61}[a-z0-9]$/
const TAG = AppEntitlementRef.typeName
const AncestralBindingTag = AncestralBindingRef.typeName
const AEUBTag = AppEntitlementUserBindingRef.typeName

const toAppEntitlementValue = (o: GetAppEntitlementResponse): AppEntitlementValue => {
    const expanded = expanderizeEntitlementResponse(o)
    const { appEntitlement, app, appResourceType, appResource } = expanded
    return {
        ...AppEntitlement.create(appEntitlement ?? {}),
        app,
        appResourceType,
        appResource,
        objectPermissions: o.appEntitlementView?.objectPermissions,
    }
}

export const getAppEntitlementApi = createUnaryApi(
    AppEntitlementsClient,
    'get',
    toAppEntitlementValue,
    TAG,
)

const bulkGetMultiGetOpts: MultiGetOpts<
    AppEntitlementRef,
    BulkGetAppEntitlementsRequest,
    BulkGetAppEntitlementsResponse,
    AppEntitlementValue
> = {
    buildRequest: (refs) => ({ appEntitlementRefs: refs }),
    transformResponse: (res) => res.list,
    refExtractor: (item) => AppEntitlementRef.create({ appId: item.appId, id: item.id }),
    tag: TAG,
}

export const getAppEntitlementMultiApi = createMultiGetApi(
    AppEntitlementsClient,
    'bulkGet',
    bulkGetMultiGetOpts,
)

export const updateProvisionerPolicyEntitlementApi = createMutationApi(
    AppEntitlementsClient,
    'updateProvisionerPolicy',
    (ae) => expandAppEntitlementView(ae.appEntitlementView, ae.expanded),
    TAG,
)

export const updateDeprovisionerPolicyEntitlementApi = createMutationApi(
    AppEntitlementsClient,
    'updateDeprovisionerPolicy',
    (ae) => expandAppEntitlementView(ae.appEntitlementView, ae.expanded),
    TAG,
)

export type AncestorWithDescendants = {
    ancestorEntitlement: AppEntitlementValue
    descendantEntitlements: Array<AppEntitlementValue>
}

const transformBulkCreateAppEntitlementProxyWithDestinationsResponse = (
    resp: BulkCreateAppEntitlementProxyWithDestinationsResponse,
): {
    appEntitlementProxyBindingsErrorMap: { [key: string]: string },
    list: Array<AncestorWithDescendants>,
} => {
    const { appEntitlementProxyBindingsErrorMap, expanded, list } = resp
    return {
        appEntitlementProxyBindingsErrorMap,
        list: list.map(({ ancestorEntitlement, descendantEntitlements }) => ({
            ancestorEntitlement: expandAppEntitlementView(ancestorEntitlement, expanded),
            descendantEntitlements: descendantEntitlements.map((v) => expandAppEntitlementView(v, expanded)),
        })),
    }
}

export const bulkCreateAppEntitlementProxyWithDestinationsApi = createMutationApi(
    AppEntitlementsProxyClient,
    'bulkCreateAppEntitlementProxyWithDestinations',
    transformBulkCreateAppEntitlementProxyWithDestinationsResponse,
    AncestralBindingTag,
)

export const enableAppEntitlementProxyApi = createMutationApi(
    AppEntitlementsProxyClient,
    'enable',
    (resp: EnableAppEntitlementProxyResponse): AppEntitlementProxyValue => {
        return expandAppEntitlementProxyView(resp.appProxyEntitlementView, resp.expanded)
    },
)

export const disableAppEntitlementProxyApi = createMutationApi(
    AppEntitlementsProxyClient,
    'disable',
    (resp: DisableAppEntitlementProxyResponse): AppEntitlementProxyValue => {
        return expandAppEntitlementProxyView(resp.appProxyEntitlementView, resp.expanded)
    },
)

export const updateEntitlementApi = createMutationApi(
    AppEntitlementsClient,
    'update',
    (ae) => expandAppEntitlementView(ae.appEntitlementView, ae.expanded),
    TAG,
)

const transformFacetResponse = (d: SearchTermsResponse): Array<FacetValue> => {
    const item = d?.facets?.facets?.[0]?.item
    return item?.oneofKind === 'value' ? item.value?.values : []
}

export const taskSearchAPI = createUnaryApi(TaskSearchServiceClient, 'getSearchTerms', transformFacetResponse)

export const appEntitlementSearchTermsAPI = createUnaryApi(
    AppEntitlementSearchServiceClient,
    'getSearchTerms',
    transformFacetResponse,
)

export const transformAppEntitlementValueArrayResponse = (
    resp: ListAppEntitlementsResponse,
): Array<AppEntitlementValue> => {
    return resp.list.map((v): CatalogEntryValue => expandAppEntitlementView(v, resp.expanded))
}

export const transformSearchAppEntitlementsWithRevokableForAppUserResponse = (
    resp: SearchAppEntitlementsWithRevokableForAppUserResponse,
): Array<AppEntitlementValueWithRevokable> => {
    return resp.list.map((v): AppEntitlementValueWithRevokable => {
        const expandedEntitlement = expandAppEntitlementView(v.appEntitlement, resp.expanded)
        return {
            ...expandedEntitlement,
            isRevokable: v.isRevokable,
        }
    })
}

export const transformSearchAppEntitlementCorrelationForAccessProfileResponse = (
    resp: SearchAppEntitlementCorrelationForAccessProfileResponse,
): Array<AppEntitlementCorrelation> => {
    return resp.list || []
}

export const transformSearchGrantReasonsResponse = (
    resp: SearchGrantReasonsResponse,
): Array<GrantOriginWithData> => {
    return resp.reasons.map((v): GrantOriginWithData => {
        return GrantOriginWithData.create({
            originType: v.derivedIdType,
            originData: [v.derivedIdData],
        })
    })
}

export const transformAppEntitlementWithOneOwnerArrayResponse = (
    resp: SearchAppEntitlementsWithOneOwnerResponse,
): Array<AppEntitlementValueWithOneOwner> => {
    return resp.list.map((v): AppEntitlementValueWithOneOwner => {
        return {
            entitlement: expandAppEntitlementView(v.entitlement, resp.expanded),
            user: v.user ?? User.create({ id: '1', displayName: 'unknown' }),
        }
    })
}

export const transformAppEntitlementWithDeactivatedOwnerArrayResponse = (
    resp: SearchAppEntitlementsWithDeactivatedOwnerResponse,
): Array<AppEntitlementValueWithDeactivatedOwner> => {
    return resp.list.map((v): AppEntitlementValueWithDeactivatedOwner => {
        return {
            entitlement: expandAppEntitlementView(v.entitlement, resp.expanded),
            entitlementOwners: v.entitlementOwners,
        }
    })
}

export const transformAppEntitlementAncestorValueArrayResponse = (
    resp: AppEntitlementSearchServiceSearchAncestorsResponse,
): Array<AncestorWithDescendants> => {
    const { expanded, list } = resp
    return list.map(({ ancestorEntitlement, descendantEntitlements }) => ({
        ancestorEntitlement: expandAppEntitlementView(ancestorEntitlement, expanded),
        descendantEntitlements: descendantEntitlements.map((v) => expandAppEntitlementView(v, expanded)),
    }))
}

export const appResourceEntitlementsAPI = createPagedListApi(
    AppEntitlementsClient,
    'listForAppResource',
    transformAppEntitlementValueArrayResponse,
    TAG,
)

export const appEntitlementsSearchApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'search',
    transformAppEntitlementValueArrayResponse,
    TAG,
)

export const searchAppEntitlementsWithExpiredApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAppEntitlementsWithExpired',
    undefined,
    AEUBTag,
)

const expandAppEntitlementUserBindingWithReasons = (
    msg: AppEntitlementUserBindingWithReasons,
    values: Any[],
): AppEntitlementUserBindingWithReasonsValue => ({
    appUser: expandValue<AppUser>(msg.appUserPath, AppUser, values),
    entitlement: expandValue<AppEntitlement>(msg.entitlementPath, AppEntitlement, values),
    app: expandValue<App>(msg.appPath, App, values),
    user: expandValue<User>(msg.userPath, User, values),
    binding: msg.binding,
    reasons: msg.reasons,
    appId: msg.appId,
    appEntitlementId: msg.appEntitlementId,
    appUserId: msg.appUserId,
    deprovisionAt: msg.deprovisionAt,
})

const transformListMembershipsResponse = (resp: ListMembershipsResponse): Array<AppEntitlementUserBindingWithReasonsValue> => {
    return resp.list.map((v) => expandAppEntitlementUserBindingWithReasons(v, resp.expanded))
}

export const listMembershipsApi = createPagedListApi(
    AppEntitlementUserBindingServiceClient,
    'listMemberships',
    transformListMembershipsResponse,
    AEUBTag,
)

export const appEntitlementsCountAncestorsApi = createUnaryApi(AppEntitlementSearchServiceClient, 'countAncestors')

export const appEntitlementsSearchAncestorsApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAncestors',
    transformAppEntitlementAncestorValueArrayResponse,
    AncestralBindingTag,
)

export const appEntitlementsSearchCountApi = createUnaryApi(
    AppEntitlementSearchServiceClient,
    'searchCount',
)

const transformAppEntitlementProxyValueArrayResponse = (
    resp: SearchAppEntitlementProxyBindingsResponse,
): Array<AppEntitlementProxyValue> => {
    return resp.list.map((v): AppEntitlementProxyValue => expandAppEntitlementProxyView(v, resp.expanded))
}

export const appEntitlementProxyBindingsSearchApi = createPagedListApi(
    AppEntitlementsProxySearchClient,
    'searchAppEntitlementProxyBindings',
    transformAppEntitlementProxyValueArrayResponse,
)

const transformExpiringAccessForUserResponse = (
    resp: AppEntitlementSearchServiceSearchExpiringAccessForUserResponse,
): Array<AppEntitlementWithUserBindingValue> => {
    return resp.list.map((v): AppEntitlementWithUserBindingValue => expandEntitlementPerAppView(v, resp.expanded))
}

export const searchExpiringAccessForUser = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchExpiringAccessForUser',
    transformExpiringAccessForUserResponse,
)

export const searchExpiringAccessForUserCount = createUnaryApi(
    AppEntitlementSearchServiceClient,
    'searchExpiringAccessForUserCount',
)

export const countStandingHighRiskGrants = createUnaryApi(AppEntitlementSearchServiceClient, 'countStandingHighRiskGrants')

export const searchStandingHighRiskGrantsForApps = createUnaryApi(AppEntitlementSearchServiceClient, 'searchStandingHighRiskGrantsForApps')

export const searchAttributeValuesApi = createPagedListApi(
    AttributeSearchClient,
    'searchAttributeValues',
)

export const appEntitlementsDeleteApi = createMutationApi(
    AppEntitlementsClient,
    'deleteIfManuallyCreated',
)

const expandSearchAppUsersWithStandingHighRiskGrantsResponse = (
    msg: AppEntitlementUserBindingExpanded,
    values: Any[],
): AppUserValueWithEntitlementValue => {
    return {
        appUser: expandValue<AppUser>(msg.appUserPath, AppUser, values),
        entitlement: expandValue<AppEntitlement>(msg.entitlementPath, AppEntitlement, values),
        app: expandValue<App>(msg.appPath, App, values),
        user: expandValue<User>(msg.userPath, User, values),
    }
}

const transformAppUserAndEntitlementArrayResponse = (
    resp: SearchAppUsersWithStandingHighRiskGrantsResponse,
): Array<AppUserValueWithEntitlementValue> => {
    return resp.list.map((v) => {
        return expandSearchAppUsersWithStandingHighRiskGrantsResponse(v, resp.expanded)
    })
}

export const searchAppUsersWithStandingHighRiskGrants = createPagedListApi(
    AppEntitlementUserBindingServiceClient,
    'searchAppUsersWithStandingHighRiskGrants',
    transformAppUserAndEntitlementArrayResponse,
)

export const listGrantOriginsApi = createUnaryApi(
    AppEntitlementUserBindingServiceClient,
    'listGrantOrigins',
)

export const searchEntitlementsWithOneOwner = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAppEntitlementsWithOneOwner',
    transformAppEntitlementWithOneOwnerArrayResponse,
)

export const searchEntitlementsWithDeactivatedOwner = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAppEntitlementsWithDeactivatedOwner',
    transformAppEntitlementWithDeactivatedOwnerArrayResponse,
)

export const searchGrantsApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchGrants',
    (resp: AppEntitlementSearchServiceSearchExpiringAccessForUserResponse): Array<AppEntitlementWithUserBindingValueWithId> => (
        resp.list.map((v): AppEntitlementWithUserBindingValueWithId => {
            const expandedEntitlement = expandAppEntitlementView(v?.entitlement, resp.expanded)
            return {
                id: `${v.appEntitlementUserBinding?.appUser?.appUser?.id}-${getRefString(AppEntitlementRef.create({
                    id: expandedEntitlement.id,
                    appId: expandedEntitlement.appId,
                }))}`,
                entitlement: expandedEntitlement,
                appEntitlementUserBinding: v?.appEntitlementUserBinding,
                grantSources: v?.appEntitlementUserBinding?.grantSources,
            }
        })
    ),
)

export const updateAutomationApi = createMutationApi(
    AppEntitlementsClient,
    'updateAutomation',
    (ae): AppEntitlementAutomation => ae.appEntitlementAutomation || AppEntitlementAutomation.create({}),
    AppEntitlementAutomationRef.typeName,
)

export const getAutomationApi = createUnaryApi(
    AppEntitlementsClient,
    'getAutomation',
    (ae): AppEntitlementAutomation => ae.appEntitlementAutomation || AppEntitlementAutomation.create({}),
    AppEntitlementAutomationRef.typeName,
)

export const searchPastGrantsApi = createPagedListApi(
    AppEntitlementUserBindingServiceClient,
    'searchPastGrants',
    (resp: SearchPastGrantsResponse): Array<AppEntitlementUserBindingHistoryValue> => (
        resp.list.map((msg) => ({
            appUser: expandValue<AppUser>(msg.appUserPath, AppUser, resp.expanded),
            entitlement: expandValue<AppEntitlement>(msg.entitlementPath, AppEntitlement, resp.expanded),
            app: expandValue<App>(msg.appPath, App, resp.expanded),
            ...msg.history,
        }))
    ),
)

export const searchGrantFeedApi = createPagedListApi(
    AppEntitlementUserBindingServiceClient,
    'searchGrantFeed',
    (resp: SearchGrantFeedResponse): Array<AppEntitlementUserBindingFeedValue> => (
        resp.list.map((msg) => ({
            appUser: expandValue<AppUser>(msg.appUserPath, AppUser, resp.expanded),
            entitlement: expandValue<AppEntitlement>(msg.entitlementPath, AppEntitlement, resp.expanded),
            app: expandValue<App>(msg.appPath, App, resp.expanded),
            ticket: expandValue<Task>(msg.ticketPath, Task, resp.expanded),
            date: msg.feed?.date,
            eventType: msg.feed?.eventType,
            ticketId: msg.feed?.ticketId,
            appEntitlementId: msg.feed?.appEntitlementId,
            appId: msg.feed?.appId,
        }))
    ),
)

export const addAutomationExclusionApi = createMutationApi(
    AppEntitlementsClient,
    'addAutomationExclusion',
)

export const removeAutomationExclusionApi = createMutationApi(
    AppEntitlementsClient,
    'removeAutomationExclusion',
)

export const listExcludedUsersForAutomationApi = createPagedListApi(
    AppEntitlementsClient,
    'listAutomationExclusions',
    undefined,
    AEUBTag,
)
export const addManuallyManagedMembersApi = createMutationApi(
    AppEntitlementsClient,
    'addManuallyManagedMembers',
    undefined,
    AEUBTag,
)

export const removeEntitlementMembershipApi = createMutationApi(
    AppEntitlementsClient,
    'removeEntitlementMembership',
)

export const forceRunDynamicGroupAutomationApi = createMutationApi(
    AppEntitlementsClient,
    'forceRunDynamicGroupAutomation',
)

export const searchAppEntitlementsForAppUserApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAppEntitlementsForAppUser',
    transformAppEntitlementValueArrayResponse,
    TAG,
)

export const searchAppEntitlementsWithRevokableForAppUserApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAppEntitlementsWithRevokableForAppUser',
    transformSearchAppEntitlementsWithRevokableForAppUserResponse,
    TAG,
)

export const searchGrantReasonsApi = createUnaryApi(
    AppEntitlementUserBindingServiceClient,
    'searchGrantReasons',
    transformSearchGrantReasonsResponse,
)

export const getAppEntitlementUserBindingApi = createUnaryApi(
    AppEntitlementUserBindingServiceClient,
    'get',
    (resp) => AppEntitlementUserBinding.create(resp.binding ?? {}),
    undefined,
)

export type DecoratedEntitlementValue = {
    task?: MinTask
    grant?: AppEntitlementUserBinding
} & AppEntitlementValue

export const searchRequestableEntitlementsForUserApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchRequestableEntitlementsForUser',
    (resp): Array<DecoratedEntitlementValue> => (
        resp.list.map((v): DecoratedEntitlementValue => expandAppEntitlementView(v, resp.expanded))
    ),
)

export const rollupEntitlementsWithGrantsApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'rollupEntitlementsWithGrants',
    undefined,
    AppEntitlementUserBindingRef.typeName,
)

export const searchExpiringAppEntitlementsCountApi = createUnaryApi(
    AppEntitlementUserBindingServiceClient,
    'searchExpiringAppEntitlementsCount',
    undefined,
    TaskCountRefreshRef.typeName,
)

const transformSearchAppEntitlementUserBindingDestinationsResponse = (
    resp: SearchAppEntitlementUserBindingDestinationsResponse,
): AppEntitlementRef[] => {
    return resp.list || []
}

const transformSearchAppEntitlementUserBindingSourcesResponse = (
    resp: SearchAppEntitlementUserBindingSourcesResponse,
): AppEntitlementRef[] => {
    return resp.list || []
}

export const searchAppEntitlementUserBindingDestinationsApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAppEntitlementUserBindingDestinations',
    transformSearchAppEntitlementUserBindingDestinationsResponse,
    AppEntitlementRef.typeName,
)

export const searchAppEntitlementUserBindingSourcesApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAppEntitlementUserBindingSources',
    transformSearchAppEntitlementUserBindingSourcesResponse,
    AppEntitlementRef.typeName,
)

export const searchAppEntitlementCorrelationForAccessProfileApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchAppEntitlementCorrelationForAccessProfile',
    transformSearchAppEntitlementCorrelationForAccessProfileResponse,
)

export const countGrantsExpiringBeforeApi = createUnaryApi(
    AppEntitlementSearchServiceClient,
    'countGrantsExpiringBefore',
)

export const searchGrantsExpiringBeforeApi = createPagedListApi(
    AppEntitlementSearchServiceClient,
    'searchGrantsExpiringBefore',
    transformExpiringAccessForUserResponse,
)

export const getAccessProfilesForGrantsApi = createUnaryApi(
    AppEntitlementSearchServiceClient,
    'getAccessProfilesForGrants',
)

// MultiGet API for access profiles - batches requests by appUserId within the same entitlement context
// Closure variables to pass context from buildRequest to refExtractor
let batchAppId = ''
let batchAppEntitlementId = ''

const accessProfilesMultiGetOpts: MultiGetOpts<
    AppEntitlementUserBindingRef,
    GetAccessProfilesForGrantsRequest,
    GetAccessProfilesForGrantsResponse,
    AccessProfilesForUser
> = {
    buildRequest: (refs: AppEntitlementUserBindingRef[]) => {
        // All refs in a batch will have the same appId and appEntitlementId (from same page context)
        const first = refs[0]
        // Store for use in refExtractor (buildRequest runs before refExtractor)
        batchAppId = first.appId
        batchAppEntitlementId = first.appEntitlementId
        // Deduplicate appUserIds - multiple components may request the same user's data
        const uniqueAppUserIds = [...new Set(refs.map((r) => r.appUserId))]
        return GetAccessProfilesForGrantsRequest.create({
            appId: first.appId,
            appEntitlementId: first.appEntitlementId,
            appUserIds: uniqueAppUserIds,
        })
    },
    transformResponse: (res: GetAccessProfilesForGrantsResponse) => res.accessProfilesForUsers,
    refExtractor: (item: AccessProfilesForUser) =>
        AppEntitlementUserBindingRef.create({
            appId: batchAppId,
            appEntitlementId: batchAppEntitlementId,
            appUserId: item.appUserId,
        }),
    maxBatchSize: 32, // Proto validation limits AppUserIds to 1-32 items
    tag: AppEntitlementUserBindingRef.typeName,
    enableWebsocketRefresh: false, // Access profiles don't have websocket notifications
}

export const accessProfilesMultiGetApi = createMultiGetApi(
    AppEntitlementSearchServiceClient,
    'getAccessProfilesForGrants',
    accessProfilesMultiGetOpts,
)
